import { useEffect, useRef, useState } from 'react';
import { deriveMarketResolutionThresholdPolicy, resolutionThresholdStateAt, } from '../../helpers/resolution-threshold.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { sdkEvents } from '../events.js';
const MAX_TIMEOUT_MS = 2_147_483_647;
/** The single client clock for one market detail threshold. */
export function useMarketResolutionThreshold(input) {
    const { config, marketPubkey, outcomeCount, resolvedAt, status, tradingStartedAt } = input;
    const reportedDiagnostics = useRef(new Set());
    const [state, setState] = useState(() => stateFor(input, deriveMarketResolutionThresholdPolicy({ config, outcomeCount })));
    const publishedState = useRef(state);
    useEffect(() => {
        const policy = deriveMarketResolutionThresholdPolicy({ config, outcomeCount });
        let timeout;
        let disposed = false;
        const refresh = () => {
            const next = stateFor({ status, tradingStartedAt, resolvedAt }, policy);
            reportInvalidPolicy(marketPubkey, next, reportedDiagnostics.current);
            if (!sameState(publishedState.current, next)) {
                publishedState.current = next;
                setState(next);
            }
            if (next.kind !== 'ready' || next.nextDropAtMs === null)
                return;
            const delayMs = Math.max(0, next.nextDropAtMs - Date.now());
            timeout = setTimeout(() => {
                if (!disposed)
                    refresh();
            }, Math.min(delayMs, MAX_TIMEOUT_MS));
        };
        refresh();
        return () => {
            disposed = true;
            if (timeout !== undefined)
                clearTimeout(timeout);
        };
    }, [config, marketPubkey, outcomeCount, resolvedAt, status, tradingStartedAt]);
    return state;
}
function stateFor(input, policy) {
    return resolutionThresholdStateAt({
        policy,
        status: input.status,
        tradingStartedAt: input.tradingStartedAt,
        resolvedAt: input.resolvedAt,
        nowTs: BigInt(Math.floor(Date.now() / 1000)),
    });
}
function reportInvalidPolicy(marketPubkey, state, reported) {
    if (state.kind !== 'unavailable' || state.reason === 'config-unavailable')
        return;
    const diagnosticKey = `${marketPubkey}:${state.reason}`;
    if (reported.has(diagnosticKey))
        return;
    reported.add(diagnosticKey);
    sdkEvents.emit('resolution-threshold:diagnostic', {
        entityHash: hashDiagnosticEntity(marketPubkey),
        reason: state.reason,
    });
}
function sameState(previous, next) {
    if (previous.kind !== next.kind)
        return false;
    if (previous.kind === 'unavailable')
        return next.kind === 'unavailable' && previous.reason === next.reason;
    if (next.kind === 'unavailable')
        return false;
    return (previous.thresholdPpb === next.thresholdPpb &&
        previous.nextDropAtMs === next.nextDropAtMs &&
        previous.frozen === next.frozen);
}
//# sourceMappingURL=use-market-resolution-threshold.js.map